import { useState, useEffect } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { db, storage } from '../firebase/config';
import { collection, addDoc, query, orderBy, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './NewRelease.css';

export default function NewRelease() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(idTokenResult.claims?.admin || false);
      }
    };
    checkAdminStatus();
  }, [user]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    collectionName: '',
    backgroundImage: null,
    featured: false
  });
  const [error, setError] = useState('');
  const [releases, setReleases] = useState([]);
  const [currentReleaseIndex, setCurrentReleaseIndex] = useState(0);

  // Fetch releases when component mounts
  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const releasesQuery = query(
          collection(db, 'releases'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(releasesQuery);
        const releasesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setReleases(releasesData);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching releases:', error);
        setError('Error loading releases');
        setIsLoading(false);
      }
    };

    fetchReleases();
  }, []);

  // Auto-rotate releases every 5 seconds (optional)
  useEffect(() => {
    if (releases.length > 1) {
      const interval = setInterval(() => {
        setCurrentReleaseIndex(prevIndex => 
          prevIndex === releases.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [releases]);

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setFormData({
        ...formData,
        backgroundImage: e.target.files[0]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Upload background image
      const imageRef = ref(storage, `releases/${formData.backgroundImage.name}`);
      await uploadBytes(imageRef, formData.backgroundImage);
      const imageUrl = await getDownloadURL(imageRef);

      const releaseData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        backgroundImage: imageUrl,
        collectionName: formData.collectionName,
        featured: formData.featured,
        createdBy: user.uid,
        createdAt: new Date()
      };

      // Save release data
      const docRef = await addDoc(collection(db, 'releases'), releaseData);
      const newRelease = { id: docRef.id, ...releaseData };
      
      // Update releases array with new release
      setReleases(prevReleases => [newRelease, ...prevReleases]);
      setCurrentReleaseIndex(0); // Show the newest release
      
      setFormData({
        name: '',
        description: '',
        price: '',
        collectionName: '',
        backgroundImage: null,
        featured: false
      });
    } catch (error) {
      setError('Error adding release: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation controls for releases
  const handlePrevRelease = () => {
    setCurrentReleaseIndex(prevIndex => 
      prevIndex === 0 ? releases.length - 1 : prevIndex - 1
    );
  };

  const handleNextRelease = () => {
    setCurrentReleaseIndex(prevIndex => 
      prevIndex === releases.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-release-container">
      {/* Release Display Section */}
      {releases.length > 0 ? (
        <div 
          className="release-showcase"
          style={{ backgroundImage: `url(${releases[currentReleaseIndex].backgroundImage})` }}
        >
          <button className="nav-btn prev-btn" onClick={handlePrevRelease}>‹</button>
          <div className="release-content">
          <h1>New Releases</h1>
            <h1>{releases[currentReleaseIndex].name}</h1>
            <p>{releases[currentReleaseIndex].description}</p>
            <div className="release-buttons">
              <button 
                className="shop-now-btn"
                onClick={() => navigate(`/product/${releases[currentReleaseIndex].id}`)}
              >
                Shop Now
              </button>
              <button 
                className="explore-collection-btn"
                onClick={() => navigate(`/collections/${releases[currentReleaseIndex].collectionName}`)}
              >
                Explore Collection
              </button>
            </div>
          </div>
          <button className="nav-btn next-btn" onClick={handleNextRelease}>›</button>
          
          {/* Release indicators */}
          {releases.length > 1 && (
            <div className="release-indicators">
              {releases.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentReleaseIndex ? 'active' : ''}`}
                  onClick={() => setCurrentReleaseIndex(index)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="no-releases">
          <p>No releases available.</p>
        </div>
      )}

      {/* shirts, pants, jackets and caps */}
      {/* Add New Release Form - Only visible to authenticated users */}
      {isAdmin && (
        <div className="form-section">
            <div className="form-container">
              <h2>Add New Release</h2>
              {error && <p className="error-message">{error}</p>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Product Name</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="price">Price</label>
                  <input
                    type="number"
                    id="price"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="collectionName">Collection Name</label>
                  <input
                    type="text"
                    id="collectionName"
                    value={formData.collectionName}
                    onChange={(e) => setFormData({ ...formData, collectionName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="backgroundImage">Background Image</label>
                  <input
                    type="file"
                    id="backgroundImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    required
                  />
                </div>

                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  <label htmlFor="featured">Mark as Featured</label>
                </div>

                <button type="submit">Add Release</button>
              </form>
            </div>
        </div>
      )}
    </div>
  );
}